import { inject, Injectable, signal, WritableSignal } from '@angular/core'
import { CCGEntryGroup, GroupedFeeProductResults } from '@app/core/models/custom/cart/entry-groups.model'
import { NewSizeEntryObject } from '@app/custom/components/modals/edit-sizes-modal/models/edit-sizes-modal.model'
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root'
import { UserIdService } from '@spartacus/core'
import { BehaviorSubject, combineLatest, filter, map, Observable, switchMap, take } from 'rxjs'

@Injectable({ providedIn: 'root' })

export class CustomCartService {
  private readonly activeCartService = inject(ActiveCartFacade)
  private readonly userIdService = inject(UserIdService)

  private entryGroupMinimumFees$: BehaviorSubject<object> = new BehaviorSubject<object>({})
  private showMinimumFees$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private disableCheckoutButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private feesAccepted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private preselectedSizes$: BehaviorSubject<CCGEntryGroup> = new BehaviorSubject<CCGEntryGroup>({})
  private newSizeEntry$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private newSizeList$: BehaviorSubject<[]> = new BehaviorSubject<[]>([])
  private newSizeAndQty$: BehaviorSubject<NewSizeEntryObject> = new BehaviorSubject<NewSizeEntryObject>({})
  private disableDoneButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private removedSizeFromCart$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  public entryGroupMinimumFeesData$ = this.entryGroupMinimumFees$.asObservable()
  public minimumFeesData$ = this.showMinimumFees$.asObservable()
  public disableCheckoutButtonData$ = this.disableCheckoutButton$.asObservable()
  public feesAcceptedData$ = this.feesAccepted$.asObservable()
  public preselectedSizesData$ = this.preselectedSizes$.asObservable()
  public newSizeEntryData$ = this.newSizeEntry$.asObservable()
  public newSizeListData$ = this.newSizeList$.asObservable()
  public newSizeAndQtyData$ = this.newSizeAndQty$.asObservable()
  public disableDoneButtonData$ = this.disableDoneButton$.asObservable()
  public displayMinimumFees: WritableSignal<boolean> = signal(false)
  public disableCheckoutButton: WritableSignal<boolean> = signal(false)
  public feesAccepted: WritableSignal<boolean> = signal(false)
  public showEditSizesLoader: WritableSignal<boolean> = signal(false)
  public removedSizeFromCartStatus$ = this.removedSizeFromCart$.asObservable()

  setEntryGroupMinimumFees (data: object): void {
    this.entryGroupMinimumFees$.next(data)
  }

  setMinimumFees (hasErrors: boolean): void {
    this.showMinimumFees$.next(hasErrors)
    this.displayMinimumFees.set(hasErrors)
  }

  setCheckoutButtonToDisabled (disabled: boolean): void {
    this.disableCheckoutButton$.next(disabled)
    this.disableCheckoutButton.set(disabled)
  }

  setFeesAccepted (accepted: boolean): void {
    this.feesAccepted$.next(accepted)
    this.feesAccepted.set(accepted)
  }

  setPreselectedSizing (sizeGroup: CCGEntryGroup) {
    this.preselectedSizes$.next(sizeGroup)
  }

  setNewSizeEntry (isNew: boolean) {
    this.newSizeEntry$.next(isNew)
  }

  setRemovedSizeStatus (isRemoved: boolean) {
    this.removedSizeFromCart$.next(isRemoved)
  }

  setNewSizeList (sizeList: []) {
    this.newSizeList$.next(sizeList)
  }

  setNewSizeAndQty (sizeAndQtyObj: NewSizeEntryObject) {
    this.newSizeAndQty$.next(sizeAndQtyObj)
  }

  setDoneButtonToDisabled (disabled: boolean) {
    this.disableDoneButton$.next(disabled)
  }

  getCurrentCartAndUserId (): Observable<[string, string]> {
    return combineLatest([
      this.activeCartService.isStable(),
      this.userIdService.takeUserId()
    ]).pipe(
      filter(([isStable, userId]) => !!isStable && !!userId),
      switchMap(([, userId]) =>
        this.activeCartService.getActiveCartId().pipe(
          filter((activeCartId): activeCartId is string => !!activeCartId),
          map((activeCartId) => [activeCartId, userId] as [string, string])
        )
      ),
      take(1)
    )
  }

  getCartTotalItems (cart: Cart): number {
    let total: number = 0

    for (const group of cart?.ccgEntryGroups ?? []) {
      for (const entry of group?.entries ?? []) {
        total += entry.quantity ?? 0
      }
    }

    return total
  }

  getTotalBrandedItems (groupFees: GroupedFeeProductResults[] | undefined, code: string): number {
    let result: number = 0
    groupFees?.map((fee: GroupedFeeProductResults) => {
      if (fee?.code?.startsWith(code)) {
        result = fee?.quantity as number
      }
    })

    return result
  }

  // return total $ of branding fees in entry group
  getTotalBrandedFees (groupFees: GroupedFeeProductResults[] | undefined, code: string): number {
    let result: number = 0
    groupFees?.map((fee: GroupedFeeProductResults) => {
      if (fee?.code?.startsWith(code)) {
        result = fee?.price?.value as number
      }
    })

    return result
  }
}
